import { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { usePatientMode } from "@/lib/patientMode";
import { Welcome, Presentation, Cotation } from "@/components/IntroSteps";
import { NeedStep } from "@/components/NeedStep";
import { EstimatedSatisfaction } from "@/components/Importance";
import { Results } from "@/components/Results";
import { NEEDS, SATISFACTION_LABELS, BESOINS_T } from "@/data/needs";

const PHASES = { WELCOME: "welcome", PRESENTATION: "presentation", COTATION: "cotation", NEEDS: "needs", ESTIMATED: "estimated", RESULTS: "results" };

export default function BesoinsModule({ initialLang }) {
  const { patientMode } = usePatientMode();
  const [lang, setLang] = useState(initialLang || "fr");
  const [phase, setPhase] = useState(PHASES.WELCOME);
  const [needIndex, setNeedIndex] = useState(0);
  const [satisfaction, setSatisfaction] = useState({});
  const [estimated, setEstimated] = useState({});

  const t = BESOINS_T[lang];
  const scale = SATISFACTION_LABELS[lang];
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const setAnswer = (needKey, itemIndex, value) => {
    setSatisfaction((prev) => {
      const arr = [...(prev[needKey] || new Array(5).fill(undefined))];
      arr[itemIndex] = value;
      return { ...prev, [needKey]: arr };
    });
  };

  const goNeedNext = () => {
    if (needIndex < NEEDS.length - 1) setNeedIndex((i) => i + 1);
    else setPhase(PHASES.ESTIMATED);
    scrollTop();
  };
  const goNeedPrev = () => {
    if (needIndex > 0) setNeedIndex((i) => i - 1);
    else setPhase(PHASES.COTATION);
    scrollTop();
  };
  const restart = () => {
    setSatisfaction({});
    setEstimated({});
    setNeedIndex(0);
    setPhase(PHASES.WELCOME);
    scrollTop();
  };

  const render = () => {
    switch (phase) {
      case PHASES.WELCOME:
        return <Welcome t={t} lang={lang} moduleKey="besoins" onStart={() => { setPhase(PHASES.PRESENTATION); scrollTop(); }} />;
      case PHASES.PRESENTATION:
        return <Presentation t={t} onPrev={() => { setPhase(PHASES.WELCOME); scrollTop(); }} onNext={() => { setPhase(PHASES.COTATION); scrollTop(); }} />;
      case PHASES.COTATION:
        return <Cotation t={t} scale={scale} onPrev={() => { setPhase(PHASES.PRESENTATION); scrollTop(); }} onNext={() => { setPhase(PHASES.NEEDS); setNeedIndex(0); scrollTop(); }} />;
      case PHASES.NEEDS: {
        const need = NEEDS[needIndex];
        return (
          <NeedStep
            need={need}
            indexNumber={needIndex + 1}
            total={NEEDS.length}
            values={satisfaction[need.key]}
            onChange={(i, v) => setAnswer(need.key, i, v)}
            onPrev={goNeedPrev}
            onNext={goNeedNext}
            lang={lang}
            t={t}
            scale={scale}
          />
        );
      }
      case PHASES.ESTIMATED:
        return (
          <EstimatedSatisfaction
            values={estimated}
            onChange={(k, v) => setEstimated((prev) => ({ ...prev, [k]: v }))}
            onPrev={() => { setPhase(PHASES.NEEDS); setNeedIndex(NEEDS.length - 1); scrollTop(); }}
            onSubmit={() => { setPhase(PHASES.RESULTS); scrollTop(); }}
            lang={lang}
            t={t}
          />
        );
      case PHASES.RESULTS:
        return <Results satisfaction={satisfaction} estimated={estimated} onRestart={restart} lang={lang} />;
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 no-print">
        {patientMode ? <span /> : (
          <Link to="/" data-testid="besoins-home-link" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">
            {t.allModules}
          </Link>
        )}
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      {render()}
    </Layout>
  );
}
